import { API_BASE_URL } from './api';

/**
 * Reading Progress Tracker Service
 * Handles viewport-based reading progress detection and backend synchronization
 */
class ProgressTracker {
  constructor() {
    this.observers = new Map(); // Map of content ID to IntersectionObserver
    this.progressData = new Map(); // Map of content ID to progress data
    this.batchQueue = new Map(); // Queue for batch updates
    this.batchTimer = null;
    this.isInitialized = false;
    this.userId = null;
    this.authToken = null;
    
    // Configuration
    this.config = {
      batchInterval: 3000, // Send batch updates every 3 seconds
      intersectionThreshold: [0, 0.25, 0.5, 0.75, 1.0], // Trigger at 0%, 25%, 50%, 75%, 100%
      minViewTime: 1000, // Minimum time (ms) to count as viewed
      maxViewTime: 300000, // Maximum tracking time per content (5 minutes)
      scrollUpdateInterval: 1000, // Update scroll position every 1 second
      progressPersistanceThreshold: 10 // Only persist progress >= 10%
    };

    // Bind methods
    this.handleIntersection = this.handleIntersection.bind(this);
    this.sendBatchUpdate = this.sendBatchUpdate.bind(this);
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    this.handleBeforeUnload = this.handleBeforeUnload.bind(this);
    
    // Setup page visibility and beforeunload handlers
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    window.addEventListener('beforeunload', this.handleBeforeUnload);
  }

  /**
   * Initialize the progress tracker with user authentication
   */
  initialize(userId, authToken) {
    this.userId = userId;
    this.authToken = authToken;
    this.isInitialized = true;
    console.log('📖 Progress Tracker initialized for user:', userId);
    
    // Start batch processing
    this.startBatchProcessing();
  }

  /**
   * Track a content element for reading progress
   */
  trackContent(element, contentData) {
    if (!this.isInitialized) {
      console.warn('Progress Tracker not initialized');
      return;
    }

    const { content_id, instance_id, chapter_number } = contentData;
    
    if (!content_id || !instance_id || !chapter_number) {
      console.warn('Missing required content data for progress tracking');
      return;
    }

    // Initialize progress data
    this.progressData.set(content_id, {
      ...contentData,
      element,
      startTime: Date.now(),
      viewTime: 0,
      lastViewTime: 0,
      isVisible: false,
      visibilityStartTime: null,
      maxScrollProgress: 0,
      currentScrollProgress: 0,
      lastProgressUpdate: 0
    });

    // Create intersection observer for this content
    const observer = new IntersectionObserver(
      this.handleIntersection,
      {
        threshold: this.config.intersectionThreshold,
        rootMargin: '0px'
      }
    );

    observer.observe(element);
    this.observers.set(content_id, observer);

    console.log('📍 Now tracking content:', content_id, 'in chapter', chapter_number);
  }

  /**
   * Stop tracking a content element
   */
  untrackContent(contentId) {
    // Clean up observer
    const observer = this.observers.get(contentId);
    if (observer) {
      observer.disconnect();
      this.observers.delete(contentId);
    }

    // Send final update if there's progress to report
    const progressData = this.progressData.get(contentId);
    if (progressData && progressData.maxScrollProgress > 0) {
      this.queueProgressUpdate(contentId);
    }

    // Clean up data
    this.progressData.delete(contentId);
    this.batchQueue.delete(contentId);
  }

  /**
   * Handle intersection observer events
   */
  handleIntersection(entries) {
    entries.forEach(entry => {
      const contentId = this.findContentIdByElement(entry.target);
      if (!contentId) return;

      const progressData = this.progressData.get(contentId);
      if (!progressData) return;

      const isVisible = entry.isIntersecting && entry.intersectionRatio > 0;
      const now = Date.now();

      if (isVisible && !progressData.isVisible) {
        // Content became visible
        progressData.isVisible = true;
        progressData.visibilityStartTime = now;
        
        // Start scroll tracking
        this.startScrollTracking(contentId);
        
      } else if (!isVisible && progressData.isVisible) {
        // Content became invisible
        progressData.isVisible = false;
        
        if (progressData.visibilityStartTime) {
          const sessionTime = now - progressData.visibilityStartTime;
          progressData.viewTime += sessionTime;
          progressData.lastViewTime = now;
        }
        
        // Stop scroll tracking
        this.stopScrollTracking(contentId);
        
        // Queue update if significant progress
        if (progressData.maxScrollProgress >= this.config.progressPersistanceThreshold) {
          this.queueProgressUpdate(contentId);
        }
      }

      // Update scroll-based progress for visible content
      if (isVisible) {
        const scrollProgress = Math.min(100, Math.max(0, entry.intersectionRatio * 100));
        progressData.currentScrollProgress = scrollProgress;
        progressData.maxScrollProgress = Math.max(progressData.maxScrollProgress, scrollProgress);
      }
    });
  }

  /**
   * Start scroll position tracking for a content item
   */
  startScrollTracking(contentId) {
    const progressData = this.progressData.get(contentId);
    if (!progressData || progressData.scrollTracker) return;

    progressData.scrollTracker = setInterval(() => {
      if (!progressData.isVisible) return;

      const element = progressData.element;
      const rect = element.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      
      // Calculate how much of the element is visible
      const visibleTop = Math.max(0, -rect.top);
      const visibleBottom = Math.min(rect.height, windowHeight - rect.top);
      const visibleHeight = Math.max(0, visibleBottom - visibleTop);
      const visibilityRatio = rect.height > 0 ? (visibleHeight / rect.height) : 0;
      
      // Update progress based on visibility
      const currentProgress = Math.min(100, visibilityRatio * 100);
      progressData.currentScrollProgress = currentProgress;
      progressData.maxScrollProgress = Math.max(progressData.maxScrollProgress, currentProgress);
      
      // Update view time
      const now = Date.now();
      if (progressData.visibilityStartTime) {
        progressData.viewTime = (now - progressData.visibilityStartTime) + progressData.lastViewTime;
      }
      
    }, this.config.scrollUpdateInterval);
  }

  /**
   * Stop scroll position tracking for a content item
   */
  stopScrollTracking(contentId) {
    const progressData = this.progressData.get(contentId);
    if (!progressData || !progressData.scrollTracker) return;

    clearInterval(progressData.scrollTracker);
    progressData.scrollTracker = null;
  }

  /**
   * Queue a progress update for batch processing
   */
  queueProgressUpdate(contentId) {
    const progressData = this.progressData.get(contentId);
    if (!progressData) return;

    // Only queue if there's meaningful progress
    if (progressData.maxScrollProgress < this.config.progressPersistanceThreshold) {
      return;
    }

    this.batchQueue.set(contentId, {
      content_id: contentId,
      instance_id: progressData.instance_id,
      chapter_number: progressData.chapter_number,
      view_progress: Math.round(progressData.maxScrollProgress * 100) / 100,
      viewport_time_seconds: Math.round(progressData.viewTime / 1000),
      scroll_position: Math.round(progressData.currentScrollProgress)
    });
  }

  /**
   * Start batch processing timer
   */
  startBatchProcessing() {
    if (this.batchTimer) return;

    this.batchTimer = setInterval(() => {
      // Queue all currently visible content for updates
      this.progressData.forEach((data, contentId) => {
        if (data.isVisible && data.maxScrollProgress > 0) {
          this.queueProgressUpdate(contentId);
        }
      });

      // Send batch update
      if (this.batchQueue.size > 0) {
        this.sendBatchUpdate();
      }
    }, this.config.batchInterval);
  }

  /**
   * Send batch progress updates to backend
   */
  async sendBatchUpdate() {
    if (this.batchQueue.size === 0 || !this.authToken) return;

    const updates = Array.from(this.batchQueue.values());
    this.batchQueue.clear();

    try {
      const response = await fetch(`${API_BASE_URL}/progress/batch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.authToken}`
        },
        body: JSON.stringify({ progress_updates: updates })
      });

      if (!response.ok) {
        throw new Error(`Progress update failed: ${response.status}`);
      }

      const result = await response.json();
      console.log(`📈 Progress batch updated: ${result.updated_count} items`);

    } catch (error) {
      console.error('Failed to send progress batch:', error);
      
      // Re-queue failed updates for retry
      updates.forEach(update => {
        this.batchQueue.set(update.content_id, update);
      });
    }
  }

  /**
   * Get current progress for a content item
   */
  getCurrentProgress(contentId) {
    const progressData = this.progressData.get(contentId);
    if (!progressData) return null;

    return {
      content_id: contentId,
      view_progress: progressData.maxScrollProgress,
      viewport_time_seconds: Math.round(progressData.viewTime / 1000),
      current_scroll_progress: progressData.currentScrollProgress,
      is_visible: progressData.isVisible
    };
  }

  /**
   * Get progress for all tracked content
   */
  getAllProgress() {
    const progress = {};
    this.progressData.forEach((data, contentId) => {
      progress[contentId] = this.getCurrentProgress(contentId);
    });
    return progress;
  }

  /**
   * Load saved progress from backend
   */
  async loadProgressForInstance(instanceId) {
    if (!this.authToken || !instanceId) return {};

    try {
      const response = await fetch(`${API_BASE_URL}/progress/instance/${instanceId}`, {
        headers: {
          'Authorization': `Bearer ${this.authToken}`
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to load progress: ${response.status}`);
      }

      const result = await response.json();
      console.log('📊 Loaded progress for instance:', instanceId);
      return result;

    } catch (error) {
      console.error('Failed to load instance progress:', error);
      return {};
    }
  }

  /**
   * Update chapter navigation position
   */
  async updateChapterPosition(instanceId, chapterNumber, scrollPosition, lastContentId) {
    if (!this.authToken) return;

    try {
      const response = await fetch(
        `${API_BASE_URL}/progress/chapter/${instanceId}/${chapterNumber}/position`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${this.authToken}`
          },
          body: JSON.stringify({
            scroll_position: scrollPosition,
            last_content_id: lastContentId
          })
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to update chapter position: ${response.status}`);
      }

      console.log(`📍 Updated chapter ${chapterNumber} position:`, scrollPosition);

    } catch (error) {
      console.error('Failed to update chapter position:', error);
    }
  }

  /**
   * Handle page visibility changes
   */
  handleVisibilityChange() {
    if (document.hidden) {
      // Page became hidden - update all visible content
      this.progressData.forEach((data, contentId) => {
        if (data.isVisible) {
          this.queueProgressUpdate(contentId);
        }
      });
      
      // Send immediate batch update
      if (this.batchQueue.size > 0) {
        this.sendBatchUpdate();
      }
    }
  }

  /**
   * Handle before page unload
   */
  handleBeforeUnload() {
    // Final progress update for all tracked content
    this.progressData.forEach((data, contentId) => {
      this.queueProgressUpdate(contentId);
    });

    // Synchronous final update (may be limited by browser)
    if (this.batchQueue.size > 0 && navigator.sendBeacon && this.authToken) {
      const updates = Array.from(this.batchQueue.values());
      const data = JSON.stringify({ progress_updates: updates });
      
      navigator.sendBeacon(
        `${API_BASE_URL}/progress/batch`,
        new Blob([data], { type: 'application/json' })
      );
    }
  }

  /**
   * Find content ID by DOM element
   */
  findContentIdByElement(element) {
    for (const [contentId, data] of this.progressData) {
      if (data.element === element) {
        return contentId;
      }
    }
    return null;
  }

  /**
   * Clean up all observers and timers
   */
  destroy() {
    // Clean up all observers
    this.observers.forEach(observer => observer.disconnect());
    this.observers.clear();

    // Clear all scroll trackers
    this.progressData.forEach(data => {
      if (data.scrollTracker) {
        clearInterval(data.scrollTracker);
      }
    });

    // Clear batch timer
    if (this.batchTimer) {
      clearInterval(this.batchTimer);
      this.batchTimer = null;
    }

    // Send final batch update
    if (this.batchQueue.size > 0) {
      this.sendBatchUpdate();
    }

    // Clear all data
    this.progressData.clear();
    this.batchQueue.clear();
    
    // Remove event listeners
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    window.removeEventListener('beforeunload', this.handleBeforeUnload);

    this.isInitialized = false;
    console.log('📖 Progress Tracker destroyed');
  }
}

// Export singleton instance
const progressTracker = new ProgressTracker();
export default progressTracker;